import React, { useState, useEffect } from "react";
import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import { Edit, DeleteOutline } from "@material-ui/icons";
import { toast } from "react-toastify";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TagModal from "../../components/TagModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  colorIcon: {
    color: "#0035a2",
  },
  colorPreview: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    margin: "0 auto",
    border: "1px solid rgba(0,0,0,0.2)",
  },
}));

const Tags = () => {
  const classes = useStyles();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [defaultIsGroup, setDefaultIsGroup] = useState(false);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const loadTags = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tags");
      setTags(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const handleOpenTagModal = (isGroup = false) => {
    setSelectedTagId(null);
    setDefaultIsGroup(isGroup);
    setModalOpen(true);
  };

  const handleEditTag = (tagId) => {
    setSelectedTagId(tagId);
    setDefaultIsGroup(false);
    setModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setModalOpen(false);
    setSelectedTagId(null);
    loadTags();
  };

  const handleDeleteTag = async (id) => {
    try {
      await api.delete(`/tags/${id}`);
      toast.success("Etiqueta removida com sucesso.");
      loadTags();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingTag(null);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingTag
            ? `Deseja realmente excluir a etiqueta ${deletingTag.name}?`
            : "Excluir etiqueta"
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <TagModal
        open={modalOpen}
        onClose={handleCloseTagModal}
        tagId={selectedTagId}
        defaultIsGroup={defaultIsGroup}
      />

      <MainHeader>
        <Title description="Crie grupos e etiquetas para classificar conversas.">
          Etiquetas
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleOpenTagModal(true)}
          >
            Novo grupo
          </Button>
          <IconButton
            className={classes.colorIcon}
            onClick={() => handleOpenTagModal(false)}
            size="large"
          >
            <AddIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">Nome</TableCell>
              <TableCell align="left">Grupo</TableCell>
              <TableCell align="left">Descrição</TableCell>
              <TableCell align="center">Cor</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {tags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell align="left">
                    {tag.name}
                    {tag.isGroup && (
                      <Chip
                        label="Grupo"
                        size="small"
                        color="primary"
                        style={{ marginLeft: 8, height: 20 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="left">{tag.parent?.name || "-"}</TableCell>
                  <TableCell align="left">{tag.description}</TableCell>
                  <TableCell align="center">
                    <div
                      className={classes.colorPreview}
                      style={{ backgroundColor: tag.color }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditTag(tag.id)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingTag(tag);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={5} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Tags;
